interface LogoProps {
  size?: number
  showWordmark?: boolean
  className?: string
}

/**
 * Logo for Orion AI · orionglobal.ai
 *
 * Mark — the seven main stars of the Orion constellation, arranged in
 * the iconic hourglass: Betelgeuse + Bellatrix on the upper edge, the
 * three belt stars (Alnitak · Alnilam · Mintaka) on a diagonal in the
 * middle, and Saiph + Rigel on the lower edge. The brightest stars
 * (Betelgeuse and Rigel) carry a soft amber-coloured glow; the outline
 * of the constellation is suggested by ultra-thin dashed lines.
 *
 * Wordmark — "Orion AI" set in display weight, with the AI picked
 * out in the amber accent and a tiny `orionglobal.ai` watermark line
 * underneath when the wordmark is shown.
 */
export function Logo({ size = 32, showWordmark = true, className = '' }: LogoProps) {
  const viewBoxMarkOnly = '0 0 56 56'
  const viewBoxWithWord = '0 0 264 56'
  const width = showWordmark ? size * (264 / 56) : size * (56 / 56)

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={showWordmark ? viewBoxWithWord : viewBoxMarkOnly}
      width={width}
      height={size}
      fill="none"
      role="img"
      aria-label="Orion AI · orionglobal.ai"
      className={className}
    >
      <title>Orion AI</title>
      <defs>
        <radialGradient id="orionStarGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.95" />
          <stop offset="55%" stopColor="currentColor" stopOpacity="0.6" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="orionBrightGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--color-orion-amber, currentColor)" stopOpacity="0.55" />
          <stop offset="60%" stopColor="var(--color-orion-amber, currentColor)" stopOpacity="0.18" />
          <stop offset="100%" stopColor="var(--color-orion-amber, currentColor)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* ───── Mark: Orion constellation ───── */}
      <g>
        {/* Outline of the constellation — ultra-thin dashed connectors */}
        <g stroke="currentColor" strokeWidth="0.5" opacity="0.22" strokeLinecap="round">
          <line x1="14" y1="14" x2="40" y2="12" strokeDasharray="1.4 2" />
          <line x1="40" y1="12" x2="36" y2="32" strokeDasharray="1.4 2" />
          <line x1="36" y1="32" x2="42" y2="44" strokeDasharray="1.4 2" />
          <line x1="42" y1="44" x2="14" y2="46" strokeDasharray="1.4 2" />
          <line x1="14" y1="46" x2="20" y2="28" strokeDasharray="1.4 2" />
          <line x1="20" y1="28" x2="14" y2="14" strokeDasharray="1.4 2" />
          {/* Belt line — slightly more visible */}
          <line x1="20" y1="28" x2="36" y2="32" strokeWidth="0.7" opacity="0.55" />
        </g>

        {/* Two brightest stars (Betelgeuse + Rigel) with amber glow */}
        <circle cx="14" cy="14" r="10" fill="url(#orionBrightGlow)" />
        <circle cx="42" cy="44" r="10" fill="url(#orionBrightGlow)" />

        {/* All seven stars */}
        {/* Betelgeuse — upper-left, brightest */}
        <circle cx="14" cy="14" r="3.2" fill="currentColor" />
        {/* Bellatrix — upper-right */}
        <circle cx="40" cy="12" r="2.2" fill="url(#orionStarGlow)" opacity="0.7" />
        <circle cx="40" cy="12" r="1.9" fill="currentColor" />
        {/* Belt left — Alnitak */}
        <circle cx="20" cy="28" r="1.4" fill="currentColor" />
        {/* Belt middle — Alnilam */}
        <circle cx="28" cy="30" r="2.1" fill="url(#orionStarGlow)" opacity="0.55" />
        <circle cx="28" cy="30" r="1.6" fill="currentColor" />
        {/* Belt right — Mintaka */}
        <circle cx="36" cy="32" r="1.4" fill="currentColor" />
        {/* Saiph — lower-left */}
        <circle cx="14" cy="46" r="2" fill="url(#orionStarGlow)" opacity="0.7" />
        <circle cx="14" cy="46" r="1.7" fill="currentColor" />
        {/* Rigel — lower-right, brightest */}
        <circle cx="42" cy="44" r="3.2" fill="currentColor" />
      </g>

      {/* ───── Wordmark ───── */}
      {showWordmark && (
        <g transform="translate(76, 0)">
          <text
            x="0"
            y="32"
            fontFamily="Geist, 'Inter Tight', Inter, system-ui, sans-serif"
            fontSize="26"
            fontWeight="600"
            fill="currentColor"
            letterSpacing="-0.5"
          >
            Orion
          </text>
          <text
            x="84"
            y="32"
            fontFamily="Geist, 'Inter Tight', Inter, system-ui, sans-serif"
            fontSize="26"
            fontWeight="300"
            fill="var(--color-orion-amber, currentColor)"
            letterSpacing="1"
          >
            AI
          </text>
          <text
            x="0"
            y="48"
            fontFamily="ui-monospace, 'JetBrains Mono', 'SF Mono', monospace"
            fontSize="8.5"
            fontWeight="400"
            fill="currentColor"
            opacity="0.5"
            letterSpacing="1.6"
          >
            ORIONGLOBAL.AI
          </text>
        </g>
      )}
    </svg>
  )
}
